import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";

import { theme } from "./theme";
import SplashScreen from "./features/splash/SplashScreen";
import Login from "./features/auth/Login";

// Customer sayfaları (rootuser + user)
import CustomerHome from "./features/customer/pages/CustomerHome";
import CustomerSettings from "./features/customer/pages/CustomerSettings";
import CustomerEdu from "./features/customer/pages/CustomerEdu";
import CustomerInfo from "./features/customer/pages/CustomerInfo";
import CustomerNotifications from "./features/customer/pages/CustomerNotifications";
import CustomerSupport from "./features/customer/pages/CustomerSupport";

// Supporter sayfaları (destek)
import SupporterLayout from "./features/supporter/pages/SupporterLayout";
import SupporterKanban from "./features/supporter/pages/SupporterKanban";
import SupporterCancelled from "./features/supporter/pages/SupporterCancelled";
import SupporterCompleted from "./features/supporter/pages/SupporterCompleted";

// Admin sayfaları
import AdminLayout from "./features/admin/pages/AdminLayout";
import AdminHome from "./features/admin/pages/AdminHome";
import AdminCompanies from "./features/admin/pages/AdminCompanies";
import AdminCompanyAdd from "./features/admin/pages/AdminCompanyAdd";

import AdminSupports from "./features/admin/pages/AdminSupports";
import AdminSupportAdd from "./features/admin/pages/AdminSupportAdd";
import AdminEdu from "./features/admin/pages/AdminEdu";
import AdminEduAdd from "./features/admin/pages/AdminEduAdd";
import AdminUsers from "./features/admin/pages/AdminUsers";
import AdminUserAdd from "./features/admin/pages/AdminUserAdd";

import AdminKnowledge from "./features/admin/pages/AdminKnowledge";
import AdminKnowledgeAdd from "./features/admin/pages/AdminKnowledgeAdd";
import AdminNotifications from "./features/admin/pages/AdminNotifications";
import AdminNotificationAdd from "./features/admin/pages/AdminNotificationAdd";

// Ortak
import SupportMessages from "./features/support_messages/SupportMessages";
import { STORAGE_KEYS, ROUTES } from "./constants/consts";
import { unlockAudio } from "./utils/playNotificationSound";
import "./App.css";

/* ─────────────────────────────────────────────
   KULLANICI TİPİ YARDIMCILARI
   ───────────────────────────────────────────── */

function getUserType(user) {
  const raw =
    user?.user_type ||
    localStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    sessionStorage.getItem(STORAGE_KEYS.USER_TYPE) ||
    "";
  return String(raw).toLowerCase().trim();
}

function isSupporter(user) {
  return getUserType(user) === "destek";
}

function isAdmin(user) {
  return getUserType(user) === "admin";
}

function isCustomer(user) {
  const t = getUserType(user);
  return t === "rootuser" || t === "user";
}

function homeFor(user) {
  const t = getUserType(user);
  if (t === "destek") return ROUTES.SUPPORTER_HOME;
  if (t === "admin") return ROUTES.ADMIN_HOME;
  if (t === "rootuser" || t === "user") return ROUTES.CUSTOMER_HOME;
  return ROUTES.LOGIN;
}

/* ─────────────────────────────────────────────
   ORTAK BİLEŞENLER
   ───────────────────────────────────────────── */

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  return children;
}

function NotFound({ user }) {
  return (
    <Box sx={{ p: 6, textAlign: "center" }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        404 — Sayfa bulunamadı
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        URL: {window.location.pathname}
      </Typography>
      <Button variant="contained" href={user ? homeFor(user) : ROUTES.LOGIN}>
        {user ? "Ana sayfaya dön" : "Giriş yap"}
      </Button>
    </Box>
  );
}

/* ─────────────────────────────────────────────
   APP
   ───────────────────────────────────────────── */

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Splash + session kontrolü
  useEffect(() => {
    const MIN_SPLASH_MS = 1200;
    const start = Date.now();

    const bootstrap = () => {
      const stored =
        localStorage.getItem(STORAGE_KEYS.USER) ||
        sessionStorage.getItem(STORAGE_KEYS.USER);

      if (stored) {
        try {
          setUser(JSON.parse(stored));
        } catch {
          localStorage.removeItem(STORAGE_KEYS.USER);
          sessionStorage.removeItem(STORAGE_KEYS.USER);
        }
      }

      const elapsed = Date.now() - start;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      setTimeout(() => setLoading(false), wait);
    };

    bootstrap();
  }, []);

  // İlk tıklamada ses kilidini aç
  useEffect(() => {
    const handleFirstClick = () => {
      unlockAudio();
      window.removeEventListener("click", handleFirstClick);
    };
    window.addEventListener("click", handleFirstClick, { once: true });
    return () => window.removeEventListener("click", handleFirstClick);
  }, []);

  const handleLoginSuccess = (userData) => setUser(userData);

  const handleLogout = () => {
    [
      STORAGE_KEYS.USER,
      STORAGE_KEYS.USERNAME,
      STORAGE_KEYS.SUNUCU_KODU,
      STORAGE_KEYS.USER_TYPE,
      STORAGE_KEYS.TOKEN,
    ].forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    setUser(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        {loading ? (
          <SplashScreen />
        ) : (
          <Routes>
            {/* ─────────── SPLASH ─────────── */}
            <Route
              path={ROUTES.SPLASH}
              element={
                <Navigate to={user ? homeFor(user) : ROUTES.LOGIN} replace />
              }
            />

            {/* ─────────── LOGIN ─────────── */}
            <Route
              path={ROUTES.LOGIN}
              element={
                user ? (
                  <Navigate to={homeFor(user)} replace />
                ) : (
                  <Login onLoginSuccess={handleLoginSuccess} />
                )
              }
            />

            {/* ─────────── CUSTOMER ─────────── */}
            <Route
              path={ROUTES.CUSTOMER_HOME}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerHome user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CUSTOMER_SETTINGS}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerSettings user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CUSTOMER_EDU}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerEdu user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CUSTOMER_INFO}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerInfo user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CUSTOMER_NOTIFICATIONS}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerNotifications
                      user={user}
                      onLogout={handleLogout}
                    />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.CUSTOMER_SUPPORT}
              element={
                <ProtectedRoute user={user}>
                  {isCustomer(user) ? (
                    <CustomerSupport user={user} onLogout={handleLogout} />
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />

            {/* ─────────── SUPPORTER ─────────── */}
            <Route
              path={ROUTES.SUPPORTER_HOME}
              element={
                <ProtectedRoute user={user}>
                  {isSupporter(user) ? (
                    <SupporterLayout user={user} onLogout={handleLogout}>
                      <SupporterKanban user={user} />
                    </SupporterLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SUPPORTER_CANCELLED}
              element={
                <ProtectedRoute user={user}>
                  {isSupporter(user) ? (
                    <SupporterLayout user={user} onLogout={handleLogout}>
                      <SupporterCancelled user={user} />
                    </SupporterLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.SUPPORTER_COMPLETED}
              element={
                <ProtectedRoute user={user}>
                  {isSupporter(user) ? (
                    <SupporterLayout user={user} onLogout={handleLogout}>
                      <SupporterCompleted user={user} />
                    </SupporterLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />

            {/* ─────────── ADMIN ─────────── */}
            <Route
              path={ROUTES.ADMIN_HOME}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminHome user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />

            {/* ─────────── ORTAK: Mesaj ekranı ─────────── */}
            <Route
              path="/support/:supportId/messages"
              element={<SupportMessages user={user} />}
            />
            <Route
              path={ROUTES.ADMIN_COMPANIES}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminCompanies user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_COMPANIES_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminCompanyAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_SUPPORTS}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminSupports user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_SUPPORTS_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminSupportAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_USERS}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminUsers user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_USERS_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminUserAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_EDU}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminEdu user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_EDU_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminEduAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_KNOWLEDGE}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminKnowledge user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_KNOWLEDGE_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminKnowledgeAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_NOTIFICATIONS}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminNotifications user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ADMIN_NOTIFICATIONS_ADD}
              element={
                <ProtectedRoute user={user}>
                  {isAdmin(user) ? (
                    <AdminLayout user={user} onLogout={handleLogout}>
                      <AdminNotificationAdd user={user} />
                    </AdminLayout>
                  ) : (
                    <Navigate to={homeFor(user)} replace />
                  )}
                </ProtectedRoute>
              }
            />
            {/* ─────────── 404 ─────────── */}
            <Route path="*" element={<NotFound user={user} />} />
          </Routes>
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
